import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Flex,
  Spinner,
  Box,
  ModalFooter,
  Collapse,
  useDisclosure,
  ModalHeader,
} from '@chakra-ui/react';
import { shipmentsApi, ShipmentState } from '@texas/api/endpoints/shipmentsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useEffect, useMemo } from 'react';
import { ShipmentItem } from './ShipmentItem';
import { ErrorDetails } from '../alert/ErrorDetails';
import { CollapseButton } from '../CollapseButton';
import { SamplesTable } from '../samples/SamplesTable';
import { SharedDisclosureProps } from '../types';
import { useTranslation } from 'react-i18next';
import { useActiveSession } from '@texas/hooks/useSession';
import { compareEnum } from '@texas/utils/helpers/enumHelpers';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { request } from '@texas/utils/helpers/httpHelpers';
import { shipmentEvents } from './events';
import { VerifyButton } from '../verifyButton/VerifyButton';

interface Props extends SharedDisclosureProps {
  shipmentId: number;
}

export function ShipmentDetailsModal(props: Props) {
  const { t } = useTranslation();
  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('shipment.details')}</ModalHeader>
        <ModalCloseButton />
        <Inner shipmentId={props.shipmentId} onClose={props.onClose} />
      </ModalContent>
    </Modal>
  );
}

function Inner({
  shipmentId,
  onClose,
}: {
  shipmentId: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { currentUser } = useActiveSession();
  const { data, refetch, loading, error } = useApiResource(
    shipmentsApi.getShipment,
  );
  const { isOpen: yourIsOpen, onToggle: onToggleYour } = useDisclosure({
    defaultIsOpen: true,
  });
  const { isOpen: othersIsOpen, onToggle: onToggleOthers } = useDisclosure({
    defaultIsOpen: true,
  });

  useEffect(() => {
    refetch(shipmentId);
  }, [refetch, shipmentId]);

  const {
    request: receivedRequest,
    loading: receivedLoading,
    error: receivedError,
  } = useApiRequest(shipmentsApi.unpack);

  const performReceived = () =>
    request(
      receivedRequest,
      [
        shipmentId,
        {
          sampleIds: data?.samplesInShipment.map((s) => s.sampleId) ?? [],
          lostSampleIds: [],
        },
      ],
      () => {
        onClose();
        shipmentEvents.onShipmentUnpacked.dispatch();
      },
    );

  const yourSamples = useMemo(() => {
    return (
      data?.samplesInShipment.filter(
        (x) => x.transport.receiverUser?.id === currentUser.id,
      ) ?? []
    );
  }, [data, currentUser]);

  const othersSamples = useMemo(() => {
    return (
      data?.samplesInShipment.filter(
        (x) => x.transport.receiverUser?.id !== currentUser.id,
      ) ?? []
    );
  }, [data, currentUser]);

  return (
    <>
      {error && <ErrorDetails error={error} />}
      {receivedError && <ErrorDetails error={receivedError} />}
      <ModalBody>
        <Flex flexDir="column" alignItems="center">
          {loading && <Spinner />}
          {data && (
            <>
              <Box w="full" py={2}>
                <ShipmentItem
                  shipment={{
                    ...data,
                    samplesInShipmentCount: data.samplesInShipment.length,
                  }}
                />
              </Box>
            </>
          )}
          {yourSamples.length > 0 && (
            <>
              <CollapseButton
                variant="texas-light"
                w="full"
                mt={4}
                mb={2}
                onToggle={onToggleYour}
                isOpen={yourIsOpen}
                label={t('shipment.yourSamples', {
                  count: yourSamples.length,
                })}
              />
              <Box w="full" mb={2}>
                <Collapse in={yourIsOpen} animateOpacity={true}>
                  <SamplesTable
                    samples={yourSamples}
                    maxH="400px"
                    overflowY="auto"
                  />
                </Collapse>
              </Box>
            </>
          )}
          {othersSamples.length > 0 && (
            <>
              <CollapseButton
                variant="texas-light"
                w="full"
                mt={2}
                mb={1}
                onToggle={onToggleOthers}
                isOpen={othersIsOpen}
                label={t('shipment.othersSamples', {
                  count: othersSamples.length,
                })}
              />
              <Box w="full" mb={2}>
                <Collapse in={othersIsOpen} animateOpacity={true}>
                  <SamplesTable
                    samples={othersSamples}
                    maxH="400px"
                    overflowY="auto"
                  />
                </Collapse>
              </Box>
            </>
          )}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Flex gap={2}>
          {compareEnum(
            ShipmentState,
            data?.state,
            ShipmentState.Dispatched,
          ) && (
            <VerifyButton
              label={t('shipment.markReceived')}
              buttonProps={{
                isDisabled: loading || receivedLoading,
                variant: 'texas-solid',
              }}
              onVerified={performReceived}
            />
          )}
        </Flex>
      </ModalFooter>
    </>
  );
}
